import { useEffect, useMemo, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import { Badge, IconButton, Popover } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { parseComments, summarizeNewest } from './parse-comments';
import './comments-badge.scss';

const AUTO_DISMISS_MS = 5000;
// Stable id so paging between expenses replaces the toast instead of stacking.
const TOAST_ID = 'expense-comments';

const CommentsBadge = ({ data, comments }) => {
  const rows = useMemo(() => parseComments(comments), [comments]);
  const anchorRef = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const summary = rows.length > 0
    ? summarizeNewest(rows, { internalLabel: data.t.read('review_expense.comments.internal') })
    : '';

  const showToast = () => toast(summary, { id: TOAST_ID, icon: '💬', duration: AUTO_DISMISS_MS });

  useEffect(() => {
    if (summary) showToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  if (rows.length === 0) return null;

  return (
    <>
      <IconButton
        ref={anchorRef}
        size='small'
        onClick={() => { setPopoverOpen(true); showToast(); }}
        data-testid='comments-badge'
        aria-label={data.t.read('review_expense.comments.aria_label')}
      >
        <Badge badgeContent={rows.length} color='primary' data-testid='comments-badge-count'>
          <FontAwesomeIcon icon={faCommentDots} />
        </Badge>
      </IconButton>

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        // Left/left: the badge sits at the far left, so a right-anchored panel
        // grows over the sidebar instead of into the space beside Back.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { 'aria-labelledby': 'comments-popover-title' } }}
      >
        <div className='comments-popover' data-testid='comments-popover'>
          <div id='comments-popover-title' className='comments-popover__header'>
            {data.t.read('review_expense.comments.title')}
          </div>
          <ul className='comments-popover__list'>
            {rows.map((row, i) => (
              <li
                key={i}
                className={[
                  'comments-popover__row',
                  row.author === 'INTERNAL' && 'comments-popover__row--internal',
                  !row.timestamp && !row.author && 'comments-popover__row--free',
                ].filter(Boolean).join(' ')}
              >
                {row.timestamp && <span className='comments-popover__ts'>{row.timestamp}</span>}
                {row.author && (
                  <span className='comments-popover__author'>
                    {row.author === 'INTERNAL' ? data.t.read('review_expense.comments.internal') : row.author}
                  </span>
                )}
                <span className='comments-popover__text'>
                  {row.text}
                  {row.count > 1 && <span className='comments-popover__count'> × {row.count}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Popover>
    </>
  );
};

export default inject('data')(CommentsBadge);
